/** Pure helpers for LineOperation path ordering and resource grouping. */

import type {
  PlantEdge,
  PlantEquipment,
  PlantLineLaborStation,
  PlantLineOperation,
  PlantOperationEquipment,
  PlantOperationOrderCoverage
} from '@axilon-apps/domains-ontology-ui-state'

export interface OperationOrder {
  ordered: PlantLineLaborStation[]
  /** Every operation has a unique, finite sequence_number. */
  complete: boolean
}

export interface OperationEquipmentLinks {
  station: PlantLineLaborStation
  /** Equipment that FEEDS material into the operation. */
  from: PlantEquipment[]
  /** Equipment linked with USES_EQUIPMENT. */
  uses: PlantEquipment[]
  /** Equipment the operation FEEDS material into. */
  to: PlantEquipment[]
}

export interface GroupedOperationEquipment {
  from: PlantEquipment[]
  uses: Array<{ equipment: PlantEquipment; usageKind?: string }>
  to: PlantEquipment[]
}

function compareStationName(a: PlantLineLaborStation, b: PlantLineLaborStation): number {
  return a.task.name.localeCompare(b.task.name) || a.task.id.localeCompare(b.task.id)
}

/** Legacy HumanLabor path ordering (unmigrated graphs). */
export function orderOperations(stations: PlantLineLaborStation[]): OperationOrder {
  const sequenceNumbers = stations.map((station) => station.task.sequence_number)
  const complete =
    sequenceNumbers.every(
      (value): value is number => typeof value === 'number' && Number.isFinite(value)
    ) && new Set(sequenceNumbers).size === stations.length

  const ordered = [...stations].sort((a, b) => {
    if (complete) return a.task.sequence_number! - b.task.sequence_number!
    return compareStationName(a, b)
  })
  return { ordered, complete }
}

function uniqueEquipment(equipment: PlantEquipment[]): PlantEquipment[] {
  const byId = new Map<string, PlantEquipment>()
  for (const item of equipment) byId.set(item.id, item)
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
}

/** Legacy FEEDS/USES mapping for unmigrated HumanLabor graphs. */
export function buildOperationEquipmentLinks(
  orderedStations: PlantLineLaborStation[],
  directEquipment: PlantEquipment[],
  flowNeighbors: PlantEquipment[],
  flowEdges: Array<Pick<PlantEdge, 'source' | 'target'> | { source: string; target: string }>
): OperationEquipmentLinks[] {
  const equipmentById = new Map<string, PlantEquipment>()
  for (const equipment of [...directEquipment, ...flowNeighbors]) {
    equipmentById.set(equipment.id, equipment)
  }
  for (const station of orderedStations) {
    for (const equipment of station.usedEquipment) equipmentById.set(equipment.id, equipment)
  }

  return orderedStations.map((station) => {
    const from: PlantEquipment[] = []
    const to: PlantEquipment[] = []
    for (const edge of flowEdges) {
      if (edge.target === station.task.id) {
        const equipment = equipmentById.get(edge.source)
        if (equipment) from.push(equipment)
      }
      if (edge.source === station.task.id) {
        const equipment = equipmentById.get(edge.target)
        if (equipment) to.push(equipment)
      }
    }
    return {
      station,
      from: uniqueEquipment(from),
      uses: uniqueEquipment(station.usedEquipment),
      to: uniqueEquipment(to)
    }
  })
}

export function isFullOrderCoverage(coverage: PlantOperationOrderCoverage): boolean {
  return coverage === 'full'
}

/** Resource summary for a LineOperation path tile. */
export function formatOperationResourceSummary(operation: PlantLineOperation): string | null {
  const hasLabor = operation.labor.length > 0
  const hasEquipment = operation.equipment.length > 0
  if (hasLabor && hasEquipment) return 'tandem'
  if (hasLabor) {
    const demand = operation.labor.reduce((sum, row) => {
      return sum + (typeof row.numEmployees === 'number' ? Math.trunc(row.numEmployees) : 0)
    }, 0)
    if (demand > 0) return demand === 1 ? '1 person' : `${demand} people`
    return 'labor'
  }
  if (hasEquipment) return 'machine'
  return null
}

/**
 * Group LineOperation `USES_EQUIPMENT` by usage_kind:
 * source → from, destination → to, everything else → uses.
 */
export function groupOperationEquipment(
  equipment: PlantOperationEquipment[]
): GroupedOperationEquipment {
  const from: PlantEquipment[] = []
  const to: PlantEquipment[] = []
  const uses: Array<{ equipment: PlantEquipment; usageKind?: string }> = []

  for (const row of equipment) {
    if (row.usageKind === 'source') {
      from.push(row.equipment)
    } else if (row.usageKind === 'destination') {
      to.push(row.equipment)
    } else {
      uses.push({ equipment: row.equipment, usageKind: row.usageKind })
    }
  }

  return {
    from: uniqueEquipment(from),
    uses: uses.sort(
      (a, b) =>
        a.equipment.name.localeCompare(b.equipment.name) ||
        a.equipment.id.localeCompare(b.equipment.id)
    ),
    to: uniqueEquipment(to)
  }
}

/** Equipment on the line that no primary operation references. */
export function otherEquipmentOnLine(
  operations: PlantLineOperation[],
  directEquipment: PlantEquipment[]
): PlantEquipment[] {
  const linked = new Set(operations.flatMap((op) => op.equipment.map((row) => row.equipment.id)))
  return directEquipment.filter((eq) => !linked.has(eq.id))
}
